import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { actionProps, selectState } from '../../utils/reduxActions';
import { transactionListActions } from './reducer';
import { DataGrid, GridColDef, GridToolbarContainer } from '@mui/x-data-grid';
import moment from 'moment-timezone';

interface Transaction {
  id?: string;
  type: string;
  amount: number;
  createdAt: string;
  balanceBefore: number;
  balanceAfter: number;
  paymentMethod?: string;
  shipping?: {
    serviceName: string;
    trackingNumber: string;
  };
}

interface TransactionHistoryProps {
  actions?: any;
  params?: any;
  history?: any;
  list?: Transaction[];
}

const CustomToolbar = () => {
  return <GridToolbarContainer></GridToolbarContainer>;
}

const TransactionHistory: React.FC<TransactionHistoryProps> = (props) => {
  const { actions, list, params } = props;

  useEffect(() => {
    actions.getTransactionList();
  }, [actions]);

  const handlePageChange = (newPage: number) => {
    actions.transactionParams({ page: newPage + 1 });
    actions.getTransactionList();
  };

  const handlePageSizeChange = (newPageSize: number) => {
    actions.transactionParams({ limit: newPageSize, page: 1 });
    actions.getTransactionList();
  };

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Hora y Fecha',
      type: 'string',
      minWidth: 180,
      sortable: false, flex: 1, align: 'center', headerAlign: 'center',
      headerClassName: 'text-lg text-bold text-gray-500',
      valueGetter: ({ value }) => value && moment(value).tz('America/Mexico_City').format("DD/MM/YYYY HH:mm"),
    },
    {
      field: 'type',
      headerName: 'Concepto',
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      minWidth: 200,
      headerClassName: 'text-lg text-bold text-gray-500',
      renderCell: (data) => {
        if (data.row.type === 'GENERATE_GUIDE') {
          return <span>Guía generada</span>;
        } else if (data.row.type === 'RECHARGE_CREDITS') {
          return <span>Recarga de Saldo</span>;
        }
        return <span>{data.row.type}</span>;
      }
    },
    {
      field: 'detalle',
      headerName: 'Detalle',
      flex: 1.5,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      minWidth: 220,
      headerClassName: 'text-lg text-bold text-gray-500',
      renderCell: (data) => {
        if (data.row.type === 'GENERATE_GUIDE' && data.row.shipping) {
          return <span>{data.row.shipping.serviceName} (Trk: {data.row.shipping.trackingNumber})</span>;
        } else if (data.row.type === 'RECHARGE_CREDITS') {
          return <span>-</span>;
        }
        return <span>-</span>;
      }
    },
    {
      field: 'balanceBefore',
      headerName: 'Saldo Anterior',
      type: 'number',
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      minWidth: 150,
      headerClassName: 'text-lg text-bold text-gray-500',
      valueFormatter: ({ value }) => new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(value as number),
    },
    {
      field: 'amount',
      headerName: 'Monto',
      type: 'number',
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      minWidth: 150,
      headerClassName: 'text-lg text-bold text-gray-500',
      renderCell: (data) => {
        const amount = data.row.amount;
        const formattedAmount = new Intl.NumberFormat('es-MX', {
          style: 'currency',
          currency: 'MXN'
        }).format(amount);

        if (data.row.type === 'GENERATE_GUIDE') {
          return <span className="text-red-500">-{formattedAmount}</span>;
        } else if (data.row.type === 'RECHARGE_CREDITS') {
          return <span className="text-green-500">+{formattedAmount}</span>;
        }
        return <span>{formattedAmount}</span>;
      }
    },
    {
      field: 'balanceAfter',
      headerName: 'Saldo Posterior',
      type: 'number',
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      minWidth: 150,
      headerClassName: 'text-lg text-bold text-gray-500',
      valueFormatter: ({ value }) => new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(value as number),
    },
  ];

  return (
    <div className="container mx-auto p-4 text-center">
      <div className="w-full text-left mb-6">
        <h1 className="text-lg lg:text-4xl font-semibold shadow-3xl my-2">Mis Movimientos</h1>
        <hr className="my-6" />
      </div>
      <div className='w-full bg-white rounded-xl shadow-md overflow-hidden'>
        <DataGrid
          autoHeight
          localeText={{
            toolbarDensity: 'Tamaño de Columnas',
            toolbarDensityLabel: 'Size',
            toolbarDensityCompact: 'Pequeño',
            toolbarDensityStandard: 'Medio',
            toolbarDensityComfortable: 'Grande',
            toolbarExport: 'Exportar',
            toolbarFilters: 'Filtros',
            toolbarColumns: 'Columnas',
            MuiTablePagination: {
              labelRowsPerPage: 'Movimientos por página:',
            },
          }}
          rows={list || []}
          columns={columns}
          loading={params?.loading}
          rowCount={params?.total || 0}
          paginationMode="server"
          page={(params?.page || 1) - 1}
          pageSize={params?.limit || 10}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[10, 20, 50]}
          components={{ Toolbar: CustomToolbar }}
          density='comfortable'
          disableSelectionOnClick={true}
          getRowId={(row) => row.id || Math.random().toString(36).substring(7)}
        />
      </div>
    </div>
  )
}

const withConnect = connect(
  selectState(
    'transactionList.params',
    'transactionList.list'
  ),
  actionProps({ ...transactionListActions }),
);

export default withRouter(withConnect(TransactionHistory));
